<template>
  <div class="space-y-12">
    <LandingPageBanner :image="heroImage">
      <AppHeaderLogo
        :to="localePath({ name: 'index' })"
        variant="white"
        class="lg:hidden flex mb-6 justify-center mx-auto w-fit drop-shadow-md"
      />

      <div class="text-center text-white text-shadow-md">
        <THeading
          :level="1"
          class="justify-center mb-4"
        >
          {{ t('homepageSearch.title') }}
        </THeading>

        <p class="mb-4 font-medium sm:mb-8 sm:text-xl">
          {{ isMobile ? t('homepageSearch.mobileSubtitle') : t('homepageSearch.subtitle') }}
        </p>
      </div>

      <div ref="searchFormEl">
        <SearchFormMobileTrigger
          class="lg:hidden"
          has-rental-type
        />
        <SearchFormBar
          class="max-lg:hidden justify-items-center"
          has-rental-type
        />
      </div>
    </LandingPageBanner>

    <TContainer>
      <ExperimentComponent experiment-key="rvz26351UpdatedBrandDifferentiatorCards">
        <template #enabled>
          <GeneralBrandPillars />
        </template>
        <template #disabled>
          <GeneralBenefits />
        </template>
      </ExperimentComponent>
    </TContainer>

    <TContainer>
      <GeneralPopularRvs />
    </TContainer>

    <TContainer>
      <Testimonials
        :title="t('guestReviews.title')"
        :description="t('guestReviews.description')"
        :items="guestReviews"
        :show-canadian-flag="isInCa"
      />
    </TContainer>

    <div class="bg-gray-50">
      <TContainer>
        <GeneralFirstTimeRenting />
      </TContainer>
    </div>

    <TContainer>
      <GeneralStartEarning />
    </TContainer>

    <TContainer>
      <GeneralNearbyLocations
        v-if="data?.nearbyUS?.length"
        :locations="data.nearbyUS"
        :title="t('searchRvsByState')"
        show-rv-count
      />

      <GeneralNearbyLocations
        v-if="data?.usCities?.length"
        :locations="data.usCities"
        :title="t('popularRvDestinationsInUS')"
        name-display="nameAndParent"
        show-rv-count
        no-sort
      />

      <GeneralNearbyLocations
        v-if="data?.nearbyCA?.length"
        :locations="data.nearbyCA"
        :title="t('searchRvsByProvince')"
        show-rv-count
      />

      <GeneralNearbyLocations
        v-if="data?.caCities?.length"
        :locations="data.caCities"
        :title="t('popularRvDestinationsInCA')"
        name-display="nameAndParent"
        show-rv-count
        no-sort
      />
    </TContainer>

    <NuxtPage />
  </div>
</template>

<script setup lang="ts">
import { type COUNTRY_SHORTS, ROUTE_BASE_NAME } from '~/constants'
import { IMAGE_HOMEPAGE_MAP } from '~/constants/image'
import type { TestimonialItem } from '~/components/testimonials/types'

definePageMeta({
  headerSearchFormVisibility: 'dynamic',
})

const { locale, t } = useI18n()
const localePath = useLocalePath()
const { isMobile } = useDevice()
const { showCanadianOwnerBanner } = useExperiment()

const { getStaticImage, presets } = useImage()
const heroImage = computed(() => {
  const suffix = isMobile ? 'mobile' : 'desktop'
  const preset = isMobile ? presets.NATIVE_MD : presets.NATIVE_XL
  const image = IMAGE_HOMEPAGE_MAP[suffix][showCanadianOwnerBanner.value ? 'canadian-owner' : 'default']

  return getStaticImage({
    path: `/web/images/${image}`,
    preset,
  }) ?? ''
})

useSeoHeadTags({
  title: computed(() => t('metaTitle')),
  description: computed(() => t('metaDesc')),
})

useHead({
  link: [
    {
      rel: 'preload',
      as: 'image',
      type: 'image/webp',
      href: heroImage,
    },
  ],
})

useSchemaOrg([
  defineWebSite({
    potentialAction: [
      defineSearchAction({
        target: localePath({ name: ROUTE_BASE_NAME.rv_search, query: { SearchAddress: '{search_term_string}' } }),
      }),
    ],
  }),
])

const searchFormEl = useTemplateRef<HTMLDivElement>('searchFormEl')
const stickyHeaderSearchFormShown = useStickyHeaderSearchFormShown()

useIntersectionObserver(searchFormEl, (entries) => {
  const entry = entries.at(-1)

  // On navigation the incoming page is mounted in a detached container, where
  // the observer reports "not intersecting". Only act on real measurements.
  if (!entry?.target.isConnected) {
    return
  }

  stickyHeaderSearchFormShown.value = !entry.isIntersecting
}, { rootMargin: isMobile ? '0px' : '-100px' })

onBeforeMount(() => {
  stickyHeaderSearchFormShown.value = false
})

const { isPotentiallyABot } = useBotScore()
const { isInCa, isInUs, isInCaOrUs } = useGeolocation()
const { getNearbyRegions } = usePointOfInterest()

async function fetchPopularCities(countryCode?: COUNTRY_SHORTS) {
  const cities = await $fetch('/api/popular-cities', {
    query: { limit: 0, countryCode },
  })

  return cities?.map((city) => {
    const destinationPath = locale.value === 'fr' ? city.destinationPathFr : city.destinationPathEn
    return {
      ...city,
      path: localePath({
        name: ROUTE_BASE_NAME.rv_rentals_destination,
        params: {
          destinationPath: destinationPathSegments(destinationPath),
        },
      }),
      distanceKm: null,
    }
  }) ?? []
}

const { data } = await useAsyncData(
  computed(() => `homepage-nearby-locations-${locale.value}`),
  async () => {
    // If the user is a bot or not in Canada or the US, return the Provinces and
    // States and popular cities in those regions
    if (isPotentiallyABot || !isInCaOrUs.value) {
      const [{ nearbyUS, nearbyCA }, usCities, caCities] = await Promise.all([
        getNearbyRegions(),
        fetchPopularCities('US'),
        fetchPopularCities('CA'),
      ])

      return { nearbyUS, nearbyCA, usCities, caCities }
    }

    // If the user is in Canada, return the Provinces and popular cities in that region
    if (isInCa.value) {
      const [{ nearbyCA }, caCities] = await Promise.all([
        getNearbyRegions('rv-rentals', 'CA'),
        fetchPopularCities('CA'),
      ])

      return { nearbyCA, nearbyUS: [], usCities: [], caCities }
    }

    // If the user is in the US, return the States and popular cities in that region
    const [{ nearbyUS }, usCities] = await Promise.all([
      getNearbyRegions('rv-rentals', 'US'),
      fetchPopularCities('US'),
    ])

    return { nearbyUS, nearbyCA: [], usCities, caCities: [] }
  })

const guestReviews = useState(`guest-reviews-${locale.value}`, () => {
  const preset = presets.LANDSCAPE_MD
  const reviewList: Array<TestimonialItem & { countryCode: COUNTRY_SHORTS }> = [
    {
      img: getStaticImage({
        path: '/web-app/testimonials/testimonial-1.webp',
        preset,
      }) ?? '',
      alt: t('guestReviews.1.alt'),
      content: t('guestReviews.1.content'),
      name: 'Erin W.',
      location: 'Alberta, CA',
      date: '2020',
      userType: 'guest',
      countryCode: 'CA',
    },
    {
      img: getStaticImage({
        path: '/web-app/testimonials/testimonial-2.webp',
        preset,
      }) ?? '',
      alt: t('guestReviews.2.alt'),
      content: t('guestReviews.2.content'),
      name: 'Yvonne M.',
      location: 'Ontario, CA',
      date: '2021',
      userType: 'guest',
      countryCode: 'CA',
    },
    {
      img: getStaticImage({
        path: '/web-app/testimonials/testimonial-3.webp',
        preset,
      }) ?? '',
      alt: t('guestReviews.3.alt'),
      content: t('guestReviews.3.content'),
      name: 'Sera O.',
      location: 'Ontario, CA',
      date: '2019',
      userType: 'guest',
      countryCode: 'CA',
    },
    {
      img: getStaticImage({
        path: '/web-app/testimonials/testimonial-4.webp',
        preset,
      }) ?? '',
      alt: t('guestReviews.4.alt'),
      content: t('guestReviews.4.content'),
      name: 'Samantha G.',
      location: 'Texas, USA',
      date: '2020',
      userType: 'guest',
      countryCode: 'US',
    },
    {
      img: getStaticImage({
        path: '/web-app/testimonials/testimonial-5.webp',
        preset,
      }) ?? '',
      alt: t('guestReviews.5.alt'),
      content: t('guestReviews.5.content'),
      name: 'Jon W.',
      location: 'Illinois, USA',
      date: '2025',
      userType: 'guest',
      countryCode: 'US',
    },
    {
      img: getStaticImage({
        path: '/web-app/testimonials/testimonial-6.webp',
        preset,
      }) ?? '',
      alt: t('guestReviews.6.alt'),
      content: t('guestReviews.6.content'),
      name: 'Tawnicia',
      location: 'Florida, USA',
      date: '2020',
      userType: 'guest',
      countryCode: 'US',
    },
  ]

  // Filter reviewList by countryCode using isInCa and isInUs. If the user is outside these 2 countries, list all reviews
  let filteredReviewList = reviewList
  if (isInCa.value) {
    filteredReviewList = reviewList.filter((review) => review.countryCode === 'CA')
  }
  else if (isInUs.value) {
    filteredReviewList = reviewList.filter((review) => review.countryCode === 'US')
  }
  // If the user is not in CA or US, don't filter reviews

  const randomIndex = Math.floor(Math.random() * filteredReviewList.length)
  const arr = filteredReviewList.splice(0, randomIndex)
  return filteredReviewList.concat(arr)
})
</script>

<i18n lang="json">
{
  "en": {
    "metaTitle": "RV Rentals from $47/night {'|'} #1 RV Rental site in North America",
    "metaDesc": "RVezy is the largest and safest RV rental marketplace in North America. Find RVs, motorhomes and travel trailers from coast to coast with $2M insurance, 24/7 roadside assistance, secure payments, and personal bilingual support.",

    "homepageSearch": {
      "title": "Find your perfect RV rental",
      "subtitle": "Explore the best travel trailers, motorhomes, and camper vans for your RV adventure.",
      "mobileSubtitle": "Explore motorhome and trailer rentals near you. Drive it, tow it, or get it delivered."
    },
    "searchRvsByProvince": "Search RVs by province",
    "searchRvsByState": "Search RVs by State",
    "popularRvDestinationsInUS": "Popular RV destinations in the US",
    "popularRvDestinationsInCA": "Popular RV destinations in Canada",
    "guestReviews": {
      "title": "Why guests love RVezy",
      "description": "Don’t just take our word for it — hear it from real travellers.",
      "1": {
        "alt": "",
        "content": "We rented a travel trailer for our family of 5 plus adventure pup. We drove down to Waterton National Park for Canada Day weekend. Great way to enjoy the beauty of Canada!"
      },
      "2": {
        "alt": "",
        "content": "Using RVezy has given us the chance to camp less stressed! We have had great experiences with RVezy and recommended them to many family members to use. Great customer service dealing with last minute changes. Awesome trip!"
      },
      "3": {
        "alt": "",
        "content": "This is our 5th year renting with RVEzy, 3rd with the same trailer. We have the best time every summer and the RV rental just makes it so much more comfortable for our family. Since we started renting with RVEzy 4 other families have started joining us and also rent from RVezy and next year another 2 families are coming! We are taking over campgrounds with RVezy!"
      },
      "4": {
        "alt": "",
        "content": "Renting a travel trailer was so easy and quick ! We are already planning our next adventure. We went to Crystal Beach Margaritaville and had a BLAST. It was great to be apart of the RVezy family for the weekend."
      },
      "5": {
        "alt": "",
        "content": "Great RV for Skydive Chicago Summerfest!"
      },
      "6": {
        "alt": "",
        "content": "This was our first camping trip, and we’re hooked! We explored the Gullah Geechee Heritage Corridor from St.Augustine to Savannah! Next stop....Charleston!"
      }
    }
  },
  "fr": {
    "metaTitle": "Locations de VR à partir de 47$/nuit {'|'} #1 Site de location de VR",
    "metaDesc": "RVezy est le plus grand et le plus sûr marché de location de véhicules récréatifs au Canada. Trouvez des véhicules récréatifs, des motorisés et des roulottes de côte à côte avec une assurance de 2 M$, une assistance routière 24/7, des paiements sécuritaires et un soutien personnel bilingue.",

    "homepageSearch": {
      "title": "Trouvez votre VR idéal",
      "subtitle": "Découvrez les meilleures roulottes et motorisés pour votre aventure en VR.",
      "mobileSubtitle": "Réservez un VR pour votre voyage. Explorez les meilleurs motorisés et roulottes près de chez vous."
    },
    "searchRvsByProvince": "Rechercher les VRs par province",
    "searchRvsByState": "Rechercher les VRs par état",
    "popularRvDestinationsInUS": "Destinations VR populaires aux États-Unis",
    "popularRvDestinationsInCA": "Destinations VR populaires au Canada",
    "guestReviews": {
      "title": "Pourquoi les voyageurs adorent RVezy",
      "description": "Ne vous fiez pas uniquement à notre avis - écoutez plutôt celui de vrais voyageurs.",
      "1": {
        "alt": "",
        "content": "Nous avons loué une roulotte pour notre famille de 5 et notre chien aventureux. Nous avons pris la route vers le parc national Waterton pour la fin de semaine de la fête du Canada. Une excellente façon de profiter de la beauté du Canada!"
      },
      "2": {
        "alt": "",
        "content": "Grâce à RVezy, nous avons pu camper sans stress! Nous avons eu une superbe expérience avec RVezy et nous les avons recommandés à de nombreux membres de notre famille. Le service à la clientèle est excellent et s’adapte aux changements de dernière minute. Un voyage génial!"
      },
      "3": {
        "alt": "",
        "content": "C’est la cinquième année que nous louons avec RVezy, la troisième avec la même roulotte. Nous passons chaque été des moments inoubliables et la location d’un VR rend notre séjour encore plus confortable pour toute la famille. Depuis que nous avons commencé à louer avec RVezy, quatre autres familles se sont jointes à nous et louent également chez RVezy. L’année prochaine, deux autres familles se joindront à nous! Nous envahissons les terrains de camping avec RVezy!"
      },
      "4": {
        "alt": "",
        "content": "Louer une roulotte a été tellement facile et rapide! Nous planifions déjà notre prochaine aventure. Nous sommes allés à Crystal Beach Margaritaville et nous nous sommes ÉCLATÉS. C’était génial de faire partie de la famille RVezy pendant la fin de semaine."
      },
      "5": {
        "alt": "",
        "content": "Super VR pour le Skydive Chicago Summerfest!"
      },
      "6": {
        "alt": "",
        "content": "C’était notre premier voyage en camping, et nous sommes devenus accros! Nous avons exploré le Guliah Geechee Heritage Corridor, de St. Augustine à Savannah! Prochaine étape… Charleston!"
      }
    }
  }
}
</i18n>
